import {
  memo, useState, useEffect, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

import usePrevious from '../../../../../hooks/usePrevious';
import { UK_INSIGHTS, UK_INSIGHTS_TRIAL } from '../../../../../data/features';
import { UKDisableMessage } from '../../../../../data/ukPrivateCompanyInsights';
import { perPageFilterDataTablet } from '../../../../../data/directory/publications/publications';
import FilingsSvc from '../../../../../services/dbServices/FilingsSvc';
import { RAILS_ENV } from '../../../../../data/environment';

import Preloader from '../../../../Preloader';
import PerPage from '../../../PremiumPublications/Pagination/PerPage';
import TableSection from './TableSection';
import TabTableSection from './TabTableSection';
import ConditionalTooltip from '../../../../UKPrivateCompanyInsights/CommonComponent/ConditionalTooltip';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

const CorporateActions = (props) => {
  let mounted = true;
  const { companyNumber, isWatchlist } = props;
  const initialized = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentData, setCurrentData] = useState({
    loading: true,
    newLoading: false,
    data: [],
  });
  const hasInsightsAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS)
  ));
  const hasInsightsTrialAccess = useSelector((state) => (
    (state.subscriptions.permissions.features || []).includes(UK_INSIGHTS_TRIAL)
  )) && !hasInsightsAccess;
  const width = useSelector(({ common }) => (
    common.width
  ));

  const callAPI = () => {
    const isFirstPage = currentPage === 1;
    const TypeIds = RAILS_ENV === 'production' ? '338' : '4414';
    const param = {
      per: Number(pageSize) + (isFirstPage ? 1 : 0),
      page: currentPage,
      identifier: `NO:${companyNumber}`,
      TypeIds,
    };
    setCurrentData({
      ...currentData,
      newLoading: !currentData.loading,
    });
    FilingsSvc.getFillings({ param })
      .then((res) => {
        if (!mounted) {
          return;
        }
        const shares = res?.data || [];

        initialized.current = true;
        if (isFirstPage) {
          setHasNextPage(shares && shares.length > Number(pageSize));
          setCurrentData({
            loading: false,
            newLoading: false,
            data: (shares || []).slice(0, Number(pageSize)),
          });
          return;
        }
        setCurrentData({
          loading: false,
          newLoading: false,
          data: shares || [],
        });

        if (res && res.length >= Number(pageSize) && hasInsightsAccess) {
          setHasNextPage(false);

          FilingsSvc.getFillings({
            param: {
              ...param,
              page: currentPage + 1,
            },
          })
            .then((innerRes) => {
              if (!mounted) {
                return;
              }
              const innerShares = innerRes?.data || [];
              setHasNextPage(innerShares && innerShares.length);
            });
        } else {
          if (!mounted) {
            return;
          }
          setHasNextPage(false);
        }
      });
  };

  useEffect(() => {
    setTimeout(() => {
      callAPI();
    }, 200);

    return () => {
      mounted = false;
    };
  }, []);

  const prevCurrentPage = usePrevious(currentPage);
  const prevPageSize = usePrevious(pageSize);
  useEffect(() => {
    if (
      !initialized.current
      || (prevCurrentPage === currentPage && prevPageSize === pageSize)
      || !hasInsightsAccess
    ) return;

    callAPI();
  }, [currentPage, pageSize]);

  const pageSizeChange = (value) => {
    if (!hasInsightsAccess) return;
    setPageSize(value);
    setCurrentPage(1);
  };

  const currentPageChange = (type) => {
    if (currentData.loading || currentData.newLoading || !hasInsightsAccess) return;
    if (type === 'prev') {
      if (currentPage === 1) return;
      setCurrentPage(currentPage - 1);
    } else {
      if (!hasNextPage) return;
      setCurrentPage(currentPage + 1);
    }
  };

  if (currentPage === 1 && !currentData.loading && !currentData.data.length) {
    return null;
  }

  const disableMessage = UKDisableMessage(hasInsightsTrialAccess);

  return (
    <div className={cx('corporate-actions', { no_access: !hasInsightsAccess && !hasInsightsTrialAccess, watchlist: isWatchlist })}>
      <div className={Styles['corporate-actions-header']}>
        Corporate Actions
      </div>
      <div className={Styles['corporate-actions-body']}>
        {currentData.loading ? (
          <div className={Styles['corporate-actions-body-loading']}>
            <Preloader centered loading static />
          </div>
        ) : (
          <>
            {!currentData.data.length ? (
              <div className={Styles['corporate-actions-no-data']}>
                <div className={Styles['corporate-actions-no-data-text']}>
                  No data currently available.
                </div>
              </div>
            ) : (
              <>
                {currentData.newLoading && (
                  <div className={Styles['corporate-actions-new-data']}>
                    <Preloader
                      centered
                      loading
                      static
                      top="90px"
                    />
                  </div>
                )}
                {width <= 1024 || isWatchlist ? (
                  <TabTableSection
                    currentPage={currentPage}
                    pageSize={pageSize}
                    currentData={currentData}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                  />
                ) : (
                  <TableSection
                    currentPage={currentPage}
                    pageSize={pageSize}
                    currentData={currentData}
                    hasInsightsAccess={hasInsightsAccess || hasInsightsTrialAccess}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      {!(currentPage === 1 && !currentData.loading && !currentData.data.length) && (
        <div className={Styles['corporate-actions-pagination']}>
          <PerPage
            selectedValue={pageSize}
            setSelectedValue={pageSizeChange}
            fixedSize
            fixedSizeFilterData={perPageFilterDataTablet}
            isDisable={!hasInsightsAccess}
            tooltipProps={{
              hasBigScreenDesign: true,
              isCentered: true,
              dynamicPosition: true,
              contentStyle: { left: 0 },
              className: Styles['corporate-actions-page-tooltip'],
            }}
            tooltipMessage={disableMessage}
            tooltipDisable={!hasInsightsAccess}
          />
          <div className={Styles['corporate-actions-navigation']}>
            <div className={Styles['corporate-actions-label']}>
              Page:
            </div>
            <ConditionalTooltip
              tooltipProps={{
                hasBigScreenDesign: true,
                isCentered: true,
                dynamicPosition: true,
                contentStyle: { left: 0 },
                className: Styles['corporate-actions-tooltip'],
              }}
              tooltipMessage={disableMessage}
              showTooltip={!hasInsightsAccess}
            >
              <div
                className={cx('corporate-actions-prev', {
                  disable: currentPage === 1 || currentData.loading || currentData.newLoading
                  || !hasInsightsAccess,
                })}
                onClick={() => currentPageChange('prev')}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                Prev.
              </div>
            </ConditionalTooltip>
            <ConditionalTooltip
              tooltipProps={{
                hasBigScreenDesign: true,
                isCentered: true,
                dynamicPosition: true,
                contentStyle: { left: 0 },
                className: Styles['corporate-actions-tooltip'],
              }}
              tooltipMessage={disableMessage}
              showTooltip={!hasInsightsAccess}
            >
              <div
                className={cx('corporate-actions-next', {
                  disable: !hasNextPage || currentData.loading || currentData.newLoading
                    || !(currentData.data.length >= Number(pageSize)) || !hasInsightsAccess,
                })}
                onClick={() => currentPageChange('next')}
              >
                Next
                <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </ConditionalTooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(CorporateActions);
